import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { apiCall, apiUpload, ApiError } from '@/lib/api-client';

export type NavStatus =
  | 'suggested'
  | 'flagged'
  | 'in_progress'
  | 'completed'
  | 'declined_by_patient'
  | 'not_required'
  | 'closed_external';

export type NavAttemptType = 'call' | 'text' | 'email' | 'other';
export type NavAttemptOutcome =
  | 'no_answer'
  | 'voicemail'
  | 'wrong_number'
  | 'reached'
  | 'refused'
  | 'callback_scheduled'
  // REORG-3 — la norma de SOMOS para un fallecimiento es nota + outreach
  // tracker. Sin este resultado el tracker no podía registrarlo, y la razón de
  // cierre `deceased` quedaba sin nada que la respalde.
  | 'deceased_reported'
  // Migración 070 + §04: el no-show de una cita se registra como intento del
  // ciclo (recordNoShowAttempt lo escribe el server; la UI nunca lo ofrece en
  // el modal de intentos — solo lo LEE en la lista del caso).
  | 'no_show'
  | 'other';
export type NavActivity =
  | 'eligibility_assessment'
  | 'care_management'
  | 'follow_up'
  | 'referral_coordination';
export type NavLevel = 'L1' | 'L2';
export type FindhelpHistory = 'none' | 'incomplete' | 'complete';
export type TimerActivity = 'precall' | 'ea' | 'espejo_findhelp' | 'documentacion' | 'otro';
export type NavNoteType = 'general' | 'ea' | 'outreach' | 'referral' | 'follow_up';

// ---------------------------------------------------------------- Wave 11

export type AttestationStatus = 'no_requerida' | 'pendiente' | 'enviada' | 'recibida' | 'rechazada';
export type PreauthStatus = 'no_requerida' | 'pendiente' | 'en_revision' | 'autorizada' | 'denegada';
export type AttachmentKind = 'form' | 'photo' | 'quote' | 'proof' | 'other';

/**
 * Las siete razones de cierre catalogadas (CEO 2026-08-09). Espejo del mapa del
 * servidor, que es quien decide: esta copia existe para no ofrecer en pantalla
 * una combinación que el servidor va a rechazar.
 */
export const CLOSED_REASON_BY_STATUS: Record<string, ClosedReasonCode[]> = {
  completed: ['completado', 'completado_pre_modulo'],
  // El miembro decidio que no.
  declined_by_patient: ['no_consent_ea', 'withdrew_after_consent'],
  // Hubo evaluacion y no hay nada que referir — o el screening fue negativo
  // y no habia nada que navegar (E-19: dos claims DISTINTOS, dos codigos).
  not_required: ['services_already_in_place', 'negative_no_needs'],
  // Causa externa: ni negativa del miembro, ni "no hacia falta".
  closed_external: ['unreachable_3_attempts', 'lost_medicaid', 'moved_out_of_region', 'deceased'],
};
export type ClosedReasonCode =
  | 'completado'
  // El trabajo se hizo antes de que el modulo existiera: claim distinto de
  // `completado`, y su evidencia es declarada, no probada.
  | 'completado_pre_modulo'
  | 'no_consent_ea'
  | 'withdrew_after_consent'
  | 'unreachable_3_attempts'
  | 'lost_medicaid'
  | 'moved_out_of_region'
  | 'deceased'
  | 'services_already_in_place'
  // E-19 (D-2 2026-08-26): screening negativo, nada que navegar — NO es un
  // rechazo. En FindHelp se dispone con la mecanica de no-consent (su unica
  // via de cierre); aca el registro dice la verdad y jamas se mezclan.
  | 'negative_no_needs'
  // Lo escribe SOLO el servidor (prior-navigation verify, 067): no se ofrece
  // en el selector, pero la pantalla tiene que poder nombrarlo (REVIEW-4).
  | 'prior_navigation_verified';

/** Un seguimiento por caso × servicio: el manual refiere POR SERVICIO. */
export interface CaseTrack {
  id: string;
  serviceKey: string;
  /** Sale de system_config (manual §VIII-§IX), no de una constante del front. */
  preauthRequired: boolean;
  attestationStatus: AttestationStatus;
  attestationSentAt: string | null;
  preauthStatus: PreauthStatus;
  preauthSentAt: string | null;
  createdAt: string;
  // REORG-3 — el referido es POR SERVICIO, y no referir es un dato, no la
  // ausencia de uno: el paso 3 se deriva de que cada track tenga una cosa o la otra.
  findhelpReferralId: string | null;
  referredAt: string | null;
  referJustification: string | null;
  notReferredReason: NotReferredReason | null;
  preauthDenialReason: string | null;
  preauthResubmittedAt: string | null;
  preauthResubmitCount: number;
  cancelledAt: string | null;
  cancelReason: string | null;
}

/**
 * Qué tan perseguido está el miembro, medido por el servidor.
 *
 * SOMOS exige dos vías distintas Y dos franjas distintas antes de dejar cerrar
 * por inubicable. Los cortes de las franjas son un default declarado
 * (`provisional: true`) hasta que SOMOS precise los suyos.
 */
export interface OutreachCoverage {
  attempts: number;
  required: number;
  types: string[];
  bands: string[];
  /** Intentos fuera de toda franja: se registraron, no prueban cobertura. */
  outOfHours: number;
  missingAttempts: number;
  missingTypes: number;
  missingBands: number;
  ok: boolean;
  bandsConfig: Record<string, [number, number]>;
  provisional: boolean;
}

/** Por qué NO se refiere un servicio. Espejo exacto del backend. */
export const NOT_REFERRED_REASONS = [
  'ya_lo_recibe', 'no_consintio', 'miembro_se_retiro', 'fuera_de_cobertura', 'otro',
] as const;
export type NotReferredReason = (typeof NOT_REFERRED_REASONS)[number];

export interface CaseAttachment {
  id: string;
  kind: AttachmentKind;
  filename: string;
  mime: string;
  size: string;
  created_at: string;
  track_id: string | null;
  service_key: string | null;
  uploaded_by: string | null;
  form_ledger_id?: string | null;
  form_type?: string | null;
  evidence_source?: string | null;
  document_date?: string | null;
  external_ref?: string | null;
  expires_at?: string | null;
  supersedes_attachment_id?: string | null;
  member_confirmed?: boolean;
  expired?: boolean;
}

/** Upload-only evidence kinds. Pathway never generates these PDFs. */
export const EVIDENCE_UPLOAD_KINDS = [
  'hrsn_screener',
  'eligibility_assessment',
  'outreach_tracker',
  'navigation_reimbursement',
  'attestation',
  'preauthorization',
  'quote',
  'proof',
  'photo',
  'other',
] as const;
export type EvidenceUploadKind = (typeof EVIDENCE_UPLOAD_KINDS)[number];

export const EVIDENCE_SOURCES = [
  'findhelp', 'member', 'provider', 'insurer', 'pathway', 'other',
] as const;
export type EvidenceSource = (typeof EVIDENCE_SOURCES)[number];

export interface UploadAttachmentMeta {
  kind?: EvidenceUploadKind | AttachmentKind;
  trackId?: string;
  formLedgerId?: string;
  evidenceSource?: EvidenceSource | string;
  documentDate?: string;
  externalRef?: string;
  expiresAt?: string;
  supersedesAttachmentId?: string;
  memberConfirmed?: boolean;
}

export interface UploadAttachmentResult {
  id: string;
  filename: string;
  size: number;
  duplicate: boolean;
  duplicateOf: string | null;
}

export interface NavQueueCase {
  id: string;
  clientId: string;
  status: NavStatus;
  priority: 'normal' | 'urgent';
  somosPriority: boolean;
  nextStepNavigator: boolean;
  findhelpHistory: FindhelpHistory;
  positiveDomains: string[];
  slaDeadline: string | null;
  outreachAttempts: number;
  isMine: boolean;
  notesPendingSync: number;
  firstName: string;
  lastName: string;
  cin: string;
  phone: string | null;
  language: string | null;
}

export interface NavigatorStats {
  available: number;
  urgent: number;
  mine: number;
  slaDueSoon: number;
  unitsToday: number;
  unitsMonth: number;
}

export type PrecallKey = 'mevs_hoy' | 'linea_negocio' | 'esmf_revisado' | 'privacidad_coi';
export type LineaNegocio = 'managed_care' | 'ffs';

export interface PrecallState {
  keys: string[];
  checks: Record<string, { value: string | null; checkedAt: string; checkedByName: string | null }>;
  completeToday: boolean;
  lineaNegocio: LineaNegocio | null;
  insurance: {
    carrier: string | null;
    planType: LineaNegocio | null;
    capturedAt: string | null;
    capturedByName: string | null;
    options: string[];
  };
}

export interface TimerState {
  /** Ultimo evento real sobre el caso, del tramo abierto. */
  lastTouchAt?: string | null;
  /** manual | switch | route_exit | idle */
  lastEndReason?: string | null;
  /** El ultimo tramo lo cerro la inactividad: reanudar pide un click. */
  needsManualResume?: boolean;
  /** Umbral configurado, en minutos. */
  idleMinutes?: number;
  running: boolean;
  startedAt: string | null;
  /** Server-derived. The browser only animates between polls. */
  elapsedSec: number;
  /** D-04: los segundos de LA SESION que se va a facturar (no del caso). */
  sessionSec: number;
  /** El id de esa sesion: la pantalla congela su numero contra este id. */
  sessionId: string | null;
  /**
   * Por que esta sesion NO se puede facturar, si es que no se puede. El
   * servidor no recorta por tope: acepta o rechaza. Cuando esto no es null,
   * la pantalla NO muestra un numero para transcribir — dice el motivo.
   */
  /** 'unknown' = esta respuesta no lo sabe (solo el detalle del caso lo resuelve). */
  billingBlocked: null | 'no_level' | 'below_minimum' | 'cap' | 'unknown';
  billingRemaining: number | null;
  /** D-04: las unidades que el servidor facturaria. La UI no las recalcula. */
  billableUnits: number | null;
  minBillableMinutes: number;
  /** Label of the open segment, if one is running. */
  currentActivity: TimerActivity | null;
  segments: number;
  /** Seconds per activity, zero-filled. */
  byActivity: Record<string, number>;
}

/** Con qué se probó un paso. `declarado` es el único que NO es prueba. */
export type EvidenceKind = 'findhelp_goal' | 'findhelp_referral' | 'declarado' | 'evento' | 'timer';

export interface ChecklistStep {
  step: number;
  done: boolean;
  auto: boolean;
  markedAuto: boolean;
  doneAt: string | null;
  doneByName: string | null;
  note: string | null;
  // REORG-3 — la pantalla necesita las dos cosas para no pintar un check que
  // miente: con qué se probó, y si se probó o sólo se declaró.
  evidenceKind: EvidenceKind | null;
  evidenceRef: string | null;
  declaredOnly: boolean;
  /** Sólo el 2 y el 4 se marcan a mano, y los dos piden su artefacto. */
  manual: boolean;
  /** Cómo se completa un paso que no es manual — para poder decírselo. */
  mechanism: string | null;
}

export interface ChecklistState {
  steps: ChecklistStep[];
  done: number;
  total: number;
}

export interface NavCaseDetail {
  id: string;
  clientId: string;
  status: NavStatus;
  priority: 'normal' | 'urgent';
  somosPriority: boolean;
  autoComputed: boolean;
  manuallyOverridden: boolean;
  nextStepNavigator: boolean;
  positiveDomains: string[];
  slaDeadline: string | null;
  outreachAttempts: number;
  attemptsRequired: number;
  /**
   * Lo que falta para poder cerrar por inubicable, MEDIDO POR EL SERVIDOR.
   *
   * La pantalla no lo recalcula: es la misma función que usa la guarda del
   * cierre. Si fueran dos implementaciones, algún día la pantalla diría "ya
   * podés cerrar" mientras el servidor lo rechaza.
   */
  outreachCoverage: OutreachCoverage;
  claimNavigatorId: string | null;
  claimNavigatorName: string | null;
  isMine: boolean;
  createdAt: string;
  closedAt: string | null;
  closedReason: string | null;
  /** D-12: el código catalogado (065/067); el texto libre es detalle secundario. */
  closedReasonCode?: ClosedReasonCode | null;
  /** §02 línea 2 — «episodio #N · el anterior cerró por {reason} en {date}». */
  episodeNumber: number;
  /** GC-3 #5: seguimientos completados de este episodio. */
  followupsDone?: number;
  previousEpisodeClosedReasonCode: string | null;
  previousEpisodeClosedAt: string | null;
  member: {
    firstName: string;
    lastName: string;
    cin: string;
    phone: string | null;
    language: string | null;
    noVoicemail: boolean;
    /** REORG-5 P1 — el perfil completo, en la pre-llamada y no recién en el EA. */
    dob: string | null;
    address: string | null;
    city: string | null;
    postalCode: string | null;
    phoneCell: string | null;
    phoneHome: string | null;
    phoneOther: string | null;
  };
  screening: {
    id: string;
    submittedAt: string | null;
    durationSeconds: number | null;
    agentName: string | null;
  } | null;
  attempts: Array<{
    id: string;
    attemptType: NavAttemptType;
    outcome: NavAttemptOutcome;
    notes: string | null;
    attemptedAt: string;
    navigatorName: string | null;
  }>;
  units: Array<{
    id: string;
    activity: NavActivity;
    memberFacing: boolean;
    minutes: number;
    units: number;
    level: NavLevel | null;
    serviceDate: string;
    documentedInFindhelp: boolean;
    createdAt: string;
    navigatorName: string | null;
  }>;
  notes: Array<{
    id: string;
    body: string;
    noteType: NavNoteType;
    createdAt: string;
    findhelpSyncedAt: string | null;
    /** 'dispatch' | 'findhelp_import' -- imported notes are born mirrored. */
    origin: string;
    authorName: string | null;
  }>;
  lifecycle: Array<{
    action: string;
    at: string;
    actorName: string | null;
    metadata: Record<string, unknown>;
  }>;
  notesPendingSync: number;
  firstContactAt: string | null;
  findhelpHistory: FindhelpHistory;
  eligibilityResult: 'L1' | 'L2' | null;
  eaId: string | null;
  eaStatus: 'draft' | 'completed' | 'no_consent' | null;
  precall: PrecallState;
  timer: TimerState;
  checklist: ChecklistState;
  /**
   * REORG-5 P1 — los nueve dominios del screening original con lo que el miembro
   * contestó. `screeningNotes.original` es el subconjunto que además tiene nota
   * del agente.
   */
  screeningAnswers: Array<{
    domain: string;
    answers: Record<string, unknown> | null;
    positive: boolean;
    notes: string | null;
  }>;
  screeningNotes: {
    original: Array<{ domain: string; notes: string; positive: boolean }>;
    appends: Array<{
      id: string;
      domain: string | null;
      body: string;
      authorName: string | null;
      createdAt: string;
    }>;
  };
  caps: {
    l2MonthUsed: number;
    l2MonthCap: number;
    l1EventUsed: number;
    l1EventCap: number;
    l1YearUsed: number;
    l1YearCap: number;
    minBillableMinutes: number;
  };
  outreachExhaustedAt: string | null;
  outreachUnblockedAt: string | null;
  outreachMaxAttempts: number;
}

export interface LogUnitsBody {
  activity: NavActivity;
  memberFacing: boolean;
  minutes: number;
  /** Required. Billing never defaults to L2. */
  level: NavLevel;
  documentedInFindhelp?: boolean;
  serviceDate?: string;
  /** What the timer measured. Units still come from `minutes`. */
  timerMinutes?: number;
  /** Optional historical ledger row. Units no longer require a pasted FindHelp form. */
  formLedgerId?: string;
}

/**
 * Units come back from the server and are never recomputed here. The browser
 * is not a source of truth for anything that gets invoiced — it only shows what
 * the server decided.
 */
export interface LogUnitsResult {
  units: number;
  level: NavLevel | null;
  remaining: number | null;
}

export const navigatorApi = {
  // NAV-B-10: queue/stats/claim se podaron — el dashboard reemplazó la cola y
  // el ownership nace en la creación del episodio, no en un claim competitivo.
  caseDetail: (id: string) => apiCall<NavCaseDetail>(`/navigator/cases/${id}`),
  logOutreach: (
    id: string,
    body: { attemptType: NavAttemptType; outcome: NavAttemptOutcome; notes?: string },
  ) =>
    apiCall<{ attempts: number; required: number; deadline: string | null; parked?: boolean }>(
      `/navigator/cases/${id}/outreach`,
      { method: 'POST', body },
    ),
  unblockOutreach: (id: string) =>
    apiCall<{ unblocked: true }>(`/navigator/cases/${id}/outreach/unblock`, { method: 'POST' }),
  logUnits: (id: string, body: LogUnitsBody) =>
    apiCall<LogUnitsResult>(`/navigator/cases/${id}/units`, { method: 'POST', body }),
  changeStatus: (
    id: string,
    body: { status: NavStatus; closedReason?: string; closedReasonCode?: ClosedReasonCode },
  ) =>
    apiCall<{ from: string; to: string; cancelledTracks: number }>(`/navigator/cases/${id}/status`, {
      method: 'PATCH',
      body,
    }),
  addNote: (id: string, body: { body: string; noteType?: NavNoteType }) =>
    apiCall<{ id: string }>(`/navigator/cases/${id}/notes`, { method: 'POST', body }),
  markNoteSynced: (noteId: string) =>
    apiCall<{ id: string }>(`/navigator/notes/${noteId}/synced`, { method: 'PATCH' }),

  // Wave 6B
  savePrecall: (
    id: string,
    checks: Array<{ key: PrecallKey; value?: string | null }>,
    // M1: el seguro se puede guardar solo, sin haber confirmado nada todavia.
    insurance?: { carrier?: string | null; planType?: LineaNegocio | null },
  ) =>
    apiCall<PrecallState>(`/navigator/cases/${id}/precall`, {
      method: 'POST',
      body: insurance ? { checks, insurance } : { checks },
    }),
  timer: (
    id: string,
    action: 'start' | 'pause',
    activity?: TimerActivity,
    reason?: 'manual' | 'route_exit' | 'route_enter',
  ) =>
    apiCall<TimerState>(`/navigator/cases/${id}/timer`, {
      method: 'POST', body: { action, activity, reason },
    }),
  /** Un evento real sobre el caso. Con tope de uno por minuto del lado del front. */
  timerTouch: (id: string) =>
    apiCall<{ touched: boolean }>(`/navigator/cases/${id}/timer/touch`, { method: 'POST' }),
  /**
   * REORG-3 — `evidenceRef` ya no es opcional en la práctica: el backend
   * rechaza un `done: true` sin artefacto (`EVIDENCE_REQUIRED`), y un paso que
   * no es manual lo rechaza siempre (`STEP_NOT_MANUAL`).
   */
  setChecklistStep: (
    id: string,
    step: number,
    body: { done: boolean; note?: string; evidenceRef?: string },
  ) =>
    apiCall<ChecklistState>(`/navigator/cases/${id}/checklist/${step}`, { method: 'PATCH', body }),
  markTrackGoal: (id: string, trackId: string, body?: { goalId?: string }) =>
    apiCall<{ id: string; goalRecordedAt: string }>(
      `/navigator/cases/${id}/tracks/${trackId}/goal`,
      { method: 'POST', body: body ?? {} },
    ),
  markTrackConsent: (id: string, trackId: string) =>
    apiCall<{ id: string; referralConsentAt: string }>(
      `/navigator/cases/${id}/tracks/${trackId}/consent`,
      { method: 'POST' },
    ),
  /** Referral done in FindHelp. The ID is optional — referred_at is the operational mark. */
  setTrackReferral: (
    id: string,
    trackId: string,
    body: { referralId?: string; justification?: string; providerName?: string; reReferral?: boolean },
  ) =>
    apiCall<ChecklistState>(`/navigator/cases/${id}/tracks/${trackId}/referral`, {
      method: 'POST', body,
    }),
  /** Que se decidió NO referir. Es un dato, no la ausencia de uno. */
  setTrackNotReferred: (
    id: string,
    trackId: string,
    body: { reason: NotReferredReason; note?: string },
  ) =>
    apiCall<ChecklistState>(`/navigator/cases/${id}/tracks/${trackId}/not-referred`, {
      method: 'POST', body,
    }),
  appendScreeningNote: (id: string, body: { body: string; domain?: string }) =>
    apiCall<{ id: string }>(`/navigator/cases/${id}/screening-notes`, { method: 'POST', body }),

  // Wave 6C -- history that happened in findhelp before this module existed
  setFindhelpHistory: (id: string, status: FindhelpHistory) =>
    apiCall<{ findhelpHistory: FindhelpHistory }>(`/navigator/cases/${id}/findhelp-history`, {
      method: 'PATCH', body: { status },
    }),
  importFindhelpNote: (
    id: string,
    body: { body: string; noteType?: NavNoteType; originalDate?: string },
  ) => apiCall<{ id: string }>(`/navigator/cases/${id}/findhelp-notes`, { method: 'POST', body }),
  importChecklistStep: (id: string, step: number) =>
    apiCall<ChecklistState>(`/navigator/cases/${id}/checklist/${step}/findhelp`, { method: 'PATCH' }),
  // Wave 12 — el familiar que aparece en el medio de la llamada
  householdMembers: (id: string) =>
    apiCall<HouseholdCandidate[]>(`/navigator/cases/${id}/household-members`),
  householdScreening: (
    id: string,
    body: { householdMemberId?: string; clientId?: string; relationshipType?: string },
  ) =>
    apiCall<HouseholdScreeningResult>(
      `/navigator/cases/${id}/household-screening`, { method: 'POST', body },
    ),

  importFindhelpContact: (id: string, body: { contactedOn?: string }) =>
    apiCall<{ firstContactAt: string | null; alreadyHad: boolean }>(
      `/navigator/cases/${id}/findhelp-contact`, { method: 'POST', body },
    ),

  dashboard: () => apiCall<NavigatorDashboard>('/navigator/me/dashboard'),
  // E35-D — «Hoy» y la agenda piden SOLO lo que se ve: las cuatro secciones
  // (o la vista filtrada) ya agrupadas y paginadas de a 25 en el servidor.
  // La cartera entera (`dashboard`) ya no se baja desde ninguna pantalla.
  hoy: async (p: HoyPedido): Promise<HoyPaginado> => {
    const r = await apiCall<HoyPaginado>(`/navigator/me/dashboard?${hoyQuery(p)}`);
    // Un API anterior a E35-D ignora `vista` y responde la cartera entera
    // (pasa si el web se publica antes que el API). Sin esta guarda la
    // pantalla leería `sections` vacío y diría «día limpio» con la cartera
    // llena: mejor el error con «reintentar» que una mentira tranquila.
    if (!r || r.vista !== 'paginada') throw new Error('NAV_HOY_FORMA_VIEJA');
    return r;
  },
  // E-35 — el dashboard ya no trae nombres: los de las filas que se ven
  // vienen de acá (hasta 100 ids; lo ajeno o cerrado el servidor lo ignora).
  caseLabels: (ids: readonly string[]) =>
    apiCall<CaseLabel[]>(
      `/navigator/me/case-labels?ids=${ids.map((id) => encodeURIComponent(id)).join(',')}`,
    ),
  // E-35 — la búsqueda de la cartera propia, en el servidor (la regla de
  // `caseFilter.ts`). Sin consulta activa devuelve los primeros `limit`.
  // POST: la consulta (un nombre o un teléfono) va en el cuerpo, nunca en la
  // URL — en la URL quedaría en los logs de acceso del servidor.
  caseSearch: (q: string, limit: number) =>
    apiCall<CaseSearchHit[]>('/navigator/me/case-search', {
      method: 'POST',
      body: { q, limit },
    }),
  // E-35c — sin sesión abierta el servidor responde 204 (apiCall devuelve
  // `undefined`); acá se normaliza a `null`, que es lo que el chip entiende.
  openSession: async (): Promise<OpenSessionChipData | null> =>
    (await apiCall<OpenSessionChipData | undefined>('/navigator/me/open-session')) ?? null,
  workspace: (id: string) => apiCall<WorkspaceFacts>(`/navigator/cases/${id}/workspace`),
  forms: (id: string) => apiCall<FormLedgerRow[]>(`/navigator/cases/${id}/forms`),
  recordForm: (id: string, body: RecordFormBody) =>
    apiCall<FormLedgerRow>(`/navigator/cases/${id}/forms`, { method: 'POST', body }),
  // F-9: «Ya lo llené en FindHelp» — declara los intentos propios sin declarar.
  confirmOutreach: (id: string) =>
    apiCall<{ confirmed: number }>(`/navigator/cases/${id}/outreach-tracker/confirm`, { method: 'POST' }),
  verifyPriorNavigation: (
    id: string,
    body: {
      memberConfirmed?: boolean;
      findhelpRef?: string;
      note?: string;
      /** Design spec §04: sin `answer` el server asume 'full' (compat). */
      answer?: PriorHelpAnswer;
    },
  ) =>
    apiCall<
      | { closed: true; status: string; reasonCode: string }
      | { closed: false; answer: string }
    >(
      `/navigator/cases/${id}/prior-navigation/verify`,
      { method: 'POST', body },
    ),
  /** Design spec §08/§11-6: el aviso del referido — canal + hora + quién. */
  notifyTrack: (
    id: string,
    trackId: string,
    body: { channel: NotifyChannel; note?: string },
  ) =>
    apiCall<{ id: string; memberNotifiedAt: string; channel: string }>(
      `/navigator/cases/${id}/tracks/${trackId}/notify`,
      { method: 'POST', body },
    ),
  /** Design spec §08: escalar a supervisión. El caso sigue siendo tuyo. */
  escalateCase: (id: string, body: { reason: EscalationReason; note: string }) =>
    apiCall<{ id: string; escalatedAt: string; reason: string }>(
      `/navigator/cases/${id}/escalate`,
      { method: 'POST', body },
    ),
  /**
   * Design spec §08/§11-7: cerrar la sesión facturable, atómico. Nivel + marca
   * del Reimbursement Tracker + nota, o el server no guarda.
   */
  closeSession: (
    id: string,
    body: {
      level?: NavLevel;
      activity?: NavActivity;
      minutes?: number;
      trackerConfirmed?: boolean;
      note: string;
      reconciliationReason?: string;
      skipBilling?: boolean;
    },
  ) =>
    apiCall<CloseSessionResult>(`/navigator/cases/${id}/session/close`, {
      method: 'POST',
      body,
    }),
  /** Design spec §09: Mis números — todo derivado, nada manual. */
  myNumbers: () => apiCall<MyNumbers>('/navigator/me/numbers'),
  /** §09 drill-down (CEO 2026-08-23): la lista detrás de cada número. */
  recentCases: (kind: RecentCasesKind) =>
    apiCall<{ kind: string; items: RecentCaseRow[] }>(
      `/navigator/me/recent-cases?kind=${encodeURIComponent(kind)}`,
    ),
  setMaterialChange: (id: string, value: boolean) =>
    apiCall<{ materialChange: boolean }>(`/navigator/cases/${id}/material-change`, {
      method: 'PATCH',
      body: { value },
    }),
  rescheduleAppointment: (appointmentId: string, scheduledAt: string) =>
    apiCall<{ id: string; scheduledAt: string; status: string }>(
      `/navigator/appointments/${appointmentId}/reschedule`,
      { method: 'PATCH', body: { scheduledAt } },
    ),
  cancelAppointment: (appointmentId: string, reason: string) =>
    apiCall<{ id: string; status: string }>(`/navigator/appointments/${appointmentId}/cancel`, {
      method: 'PATCH',
      body: { reason },
    }),
  completeAppointment: (appointmentId: string) =>
    apiCall<{ id: string; status: string }>(
      `/navigator/appointments/${appointmentId}/complete`,
      { method: 'PATCH' },
    ),
  noShowAppointment: (appointmentId: string) =>
    apiCall<{
      id: string;
      status: string;
      followupId: string | null;
      /** Design spec §04: el no-show cuenta como intento del ciclo. */
      attempts?: number;
      parked?: boolean;
    }>(
      `/navigator/appointments/${appointmentId}/no-show`,
      { method: 'PATCH' },
    ),
  completeFollowup: (
    followupId: string,
    body: { outreachFormId?: string; terminalOutcome?: string; reimbursementFormId?: string },
  ) =>
    apiCall<{ id: string; terminal: boolean; nextFollowupId: string | null }>(
      `/navigator/followups/${followupId}/complete`,
      { method: 'POST', body },
    ),

  // Wave 11 -- tracks and attachments. Pathway does not generate attestation
  // or preauth PDFs; DocumentsPanel is upload-only.
  tracks: (id: string) => apiCall<CaseTrack[]>(`/navigator/cases/${id}/tracks`),
  addTrack: (id: string, serviceKey: string) =>
    apiCall<CaseTrack>(`/navigator/cases/${id}/tracks`, { method: 'POST', body: { serviceKey } }),
  setAttestationStatus: (trackId: string, status: AttestationStatus) =>
    apiCall<{ trackId: string; status: string }>(`/navigator/tracks/${trackId}/attestation-status`, {
      method: 'PATCH', body: { status },
    }),
  setPreauthStatus: (trackId: string, status: PreauthStatus, denialReason?: string) =>
    apiCall<{ trackId: string; status: string }>(`/navigator/tracks/${trackId}/preauth-status`, {
      method: 'PATCH', body: { status, denialReason },
    }),
  /** Corregir y re-someter: todo el procedimiento que SOMOS tiene para una denegada. */
  resubmitPreauth: (trackId: string) =>
    apiCall<{ trackId: string; status: string; resubmitCount: number }>(
      `/navigator/tracks/${trackId}/preauth-resubmit`, { method: 'POST' },
    ),
  /**
   * C-3: el outcome del servicio — la única vía para que un track llegue a
   * `terminal` y el episodio pueda cerrar. Sin esto, `close_episode` era
   * inalcanzable desde la UI nueva.
   */
  setServiceOutcome: (
    trackId: string,
    body: { serviceStatus?: string; serviceResult?: string; serviceEndDate?: string },
  ) =>
    apiCall<{ trackId: string; serviceStatus: string; serviceResult: string | null }>(
      `/navigator/tracks/${trackId}/service`, { method: 'PATCH', body },
    ),
  attachments: (id: string) => apiCall<CaseAttachment[]>(`/navigator/cases/${id}/attachments`),
  uploadAttachment: (id: string, file: File, meta: UploadAttachmentMeta = {}) => {
    const form = new FormData();
    form.append('file', file);
    if (meta.kind) form.append('kind', meta.kind);
    if (meta.trackId) form.append('trackId', meta.trackId);
    if (meta.formLedgerId) form.append('formLedgerId', meta.formLedgerId);
    if (meta.evidenceSource) form.append('evidenceSource', meta.evidenceSource);
    if (meta.documentDate) form.append('documentDate', meta.documentDate);
    if (meta.externalRef) form.append('externalRef', meta.externalRef);
    if (meta.expiresAt) form.append('expiresAt', meta.expiresAt);
    if (meta.supersedesAttachmentId) form.append('supersedesAttachmentId', meta.supersedesAttachmentId);
    if (meta.memberConfirmed === true) form.append('memberConfirmed', 'true');
    return apiUpload<UploadAttachmentResult>(`/navigator/cases/${id}/attachments`, form);
  },
};

/**
 * Bajar el adjunto. No pasa por `apiCall` porque la respuesta es un archivo, no
 * JSON — mismo patrón que la exportación de reportes de admin. Cada descarga
 * queda auditada del lado del servidor.
 */
export async function downloadAttachment(
  attachmentId: string,
  filename: string,
  accessToken: string,
): Promise<void> {
  // NAV-B-14: misma base que api-client (respeta VITE_API_BASE) en vez del
  // path raíz hardcodeado.
  const base = ((import.meta.env.VITE_API_BASE as string | undefined) ?? '') + '/api';
  const res = await fetch(`${base}/navigator/attachments/${attachmentId}/download`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

/**
 * NestJS's exception filter wraps a thrown object under `message`, so the code
 * can sit at either depth depending on how the error was raised. Reading only
 * the top level is how every 409 in the bookings modal silently fell through to
 * a generic toast for three weeks (v0.3.2).
 */
interface NavErrorShape {
  code?: string;
  released?: boolean;
  message?: string;
  remaining?: number;
  used?: number;
  cap?: number;
  minimumMinutes?: number;
  scope?: string;
  allowed?: string[];
}

export function navError(err: unknown): NavErrorShape & { httpStatus: number | null } {
  if (!(err instanceof ApiError)) return { httpStatus: null };
  const p = err.payload as unknown as { message?: unknown; code?: unknown } | null;
  const nested = p && typeof p.message === 'object' && p.message !== null ? (p.message as NavErrorShape) : null;
  const flat = (p ?? {}) as NavErrorShape;
  return { ...flat, ...(nested ?? {}), httpStatus: err.status };
}

/** Query keys — one factory so an invalidate can never miss by a typo. */
/**
 * Un miembro del hogar del caso, con TODO su estado — la lista tiene que poder
 * decidir sola si ofrece screenearlo, continuar el screening que ya empezó, o
 * mandarte al caso que ya nació. Antes venía sólo `promoted_to_client_id` y por
 * eso el botón seguía invitando a repetir la operación después de recargar.
 */
export interface HouseholdCandidate {
  id: string;
  full_name: string | null;
  dob: string | null;
  relationship_type: string | null;
  has_medicaid: boolean | null;
  status: string | null;
  promoted_to_client_id: string | null;
  promoted_at: string | null;
  child_case_id: string | null;
  child_screening_id: string | null;
  child_screening_submitted: boolean | null;
}

export interface HouseholdScreeningResult {
  clientId: string;
  assignmentId: string;
  screeningId: string;
  originCaseId: string;
  reusedClient: boolean;
  reusedAssignment: boolean;
  /** El POST fue un reintento: este screening ya existía. */
  reusedScreening: boolean;
  /** Ya está enviado: no hay wizard que abrir. */
  alreadySubmitted: boolean;
  childCaseId: string | null;
}

/** The FindHelp forms. Pathway stores evidence, it does not fill the form. */
export const NAV_FORM_TYPES = [
  'hrsn_screener',
  'eligibility_assessment',
  'outreach_tracker',
  'navigation_reimbursement',
  // CEO 2026-08-23: la marca declarada de «sin consentimiento» en FindHelp
  // tras el cierre no_consent_ea (migración 073).
  'no_consent_findhelp',
  // E-19 (2026-08-26): la disposición del NEGATIVO en FindHelp tras el cierre
  // negative_no_needs (migración 076). Tipo propio: jamás se liga al no-consent.
  'negative_closed_findhelp',
] as const;
export type NavFormType = (typeof NAV_FORM_TYPES)[number];
export type FormLedgerStatus = 'draft' | 'submitted' | 'verified' | 'pending_evidence';

export interface FormLedgerRow {
  id: string;
  formType: NavFormType;
  status: FormLedgerStatus;
  findhelpId: string | null;
  confirmation: string | null;
  submittedAt: string | null;
  verifiedAt: string | null;
  actorName: string | null;
  contactAttemptId: string | null;
  workSessionId: string | null;
  level: NavLevel | null;
  activity: string | null;
  reportedMinutes: number | null;
  timerMinutes: number | null;
  billableUnits: number | null;
  exceptionReason: string | null;
  attachmentCount: number;
  createdAt: string;
}

export interface RecordFormBody {
  formType: NavFormType;
  status?: FormLedgerStatus;
  findhelpId?: string;
  confirmation?: string;
  submittedAt?: string;
  contactAttemptId?: string;
  workSessionId?: string;
  eaId?: string;
  level?: NavLevel;
  activity?: string;
  reportedMinutes?: number;
  exceptionReason?: string;
  reconciliationReason?: string;
}

/**
 * C-8: solo los estados que el motor emite de verdad. Los tres fantasma
 * (submit_outreach / resolve_required_document / submit_reimbursement) se
 * podaron a la vez del backend — un key nuevo acá SIN rama de CTA en
 * NextActionCard es un bug, no una opción.
 *
 * Design spec 2026-08-19 (sección 11): register_ea (la EA transcrita y marcada
 * en FindHelp — bloquea el referral), notify_member (después del referral se le
 * avisa al miembro) y close_session (nivel + tracker + nota, o no guarda).
 */
export type NextActionKey =
  | 'call_or_schedule'
  | 'verify_prior_navigation'
  | 'complete_ea'
  | 'register_ea'
  | 'review_needs'
  | 'record_goal'
  | 'confirm_referral_consent'
  | 'record_referral'
  | 'notify_member'
  | 'close_session'
  | 'complete_followup'
  | 'continue_service_followup'
  | 'close_episode'
  | 'attempts_exhausted'
  | 'closed';

export interface NextAction {
  key: NextActionKey;
  title: string;
  reason: string;
  /** D-01: clave i18n de la razón; `reason`/`title` quedan solo de fallback. */
  reasonKey?: string | null;
  params?: { service?: string } | null;
  trackId: string | null;
  formType: string | null;
  dueAt: string | null;
  findhelpUrl: string | null;
  blocked: boolean;
}

export interface WorkspaceGate {
  pendingEvidence: number;
  missingOutreach: number;
  unreimbursedSessions: number;
  canBill: boolean;
  canClose: boolean;
}

export interface WorkspaceService {
  id: string;
  domainKey: string | null;
  serviceKey: string;
  serviceStatus: string;
  serviceResult: string | null;
  findhelpGoalId: string | null;
  goalRecordedAt: string | null;
  referralConsentAt: string | null;
  findhelpReferralId: string | null;
  referredAt: string | null;
  attestationStatus: string;
  preauthStatus: string;
  /** F5-T4/N4: el bloqueo duro y la denegada llegan al workspace. */
  preauthRequired?: boolean;
  preauthDenialReason?: string | null;
  /** Design spec §11-6: el aviso del referido al miembro (canal + hora). */
  memberNotifiedAt?: string | null;
  memberNotifiedChannel?: string | null;
  nextFollowupAt: string | null;
  terminalAt: string | null;
  notReferredReason: string | null;
  /** D-17: lo escrito al no referir. Obligatorio cuando el motivo es «otro». */
  referJustification: string | null;
  cancelledAt: string | null;
  /**
   * V1.2 — a qué organización se refirió, por nombre. Lo escribe la navigator:
   * Pathway no tiene catálogo de proveedores, el proveedor se elige EN findhelp.
   * NULL en los tracks referidos antes de la migración 072.
   */
  providerName?: string | null;
  /** V1.2 — los intentos que NO funcionaron, más nuevo primero. */
  referralHistory?: WorkspaceReferralAttempt[];
}

/** V1.2 — un referido abandonado: a quién se mandó al miembro y por qué no salió. */
export interface WorkspaceReferralAttempt {
  providerName: string | null;
  referredAt: string | null;
  endedAt: string | null;
  reason: string | null;
}

export interface WorkspaceAppointment {
  id: string;
  scheduledAt: string;
  status: string;
  apptType: string | null;
  notes: string | null;
  navigationRescheduleCount: number | null;
  navigationCancelReason: string | null;
  navigationNoShowAt: string | null;
}

export interface WorkspaceFollowup {
  id: string;
  trackId: string | null;
  scope: string | null;
  ruleKey: string;
  dueDate: string;
  status: string;
  bookingId: string | null;
  terminalOutcome: string | null;
  /** C-10: calculado server-side con calendario NY — la UI no recalcula. */
  isDue?: boolean;
  /** D-05: clave del servicio del track, para etiquetar sin buscar el track. */
  serviceKey?: string | null;
}

export interface WorkspaceSession {
  id: string;
  startedAt: string;
  endedAt: string | null;
  endReason: string | null;
  reportedMinutes: number | null;
  timerMinutes: number | null;
  deltaMinutes: number | null;
  reconciliationStatus: string | null;
  navigatorId?: string | null;
  navigatorName?: string | null;
  reconciliationReason?: string | null;
  billableEligible?: boolean;
}

export interface WorkspaceFacts {
  /**
   * D-04: que seguimiento propondria el cierre si se cerrara ahora — regla y
   * fecha— para que el paso de agendar pueda dibujarse ANTES de que la fila
   * exista. `null` si no hay nada esperando al proveedor, o si ya se decidio.
   */
  followupProposal: { ruleKey: string; dueDate: string; waiting: number } | null;
  caseId: string;
  owner: { id: string; name: string | null };
  operationalStatus: string;
  /** F5-T4/N2-c: riesgo de seguridad del screening de origen. */
  safetyConcern?: boolean;
  nextAction: NextAction;
  ea: {
    assessmentId: string | null;
    completedAt: string | null;
    validUntil: string | null;
    reusable: boolean;
    hasFindhelpEvidence: boolean;
    reason: string;
    /** D-02: código tipado; la UI traduce y NUNCA parsea el texto inglés. */
    reasonCode?: 'none' | 'material_change' | 'expired' | 'reusable';
  };
  priorNavigation: {
    history: string;
    verifiedAt: string | null;
    memberConfirmed: boolean;
    findhelpRef: string | null;
    /** Design spec §04: la respuesta a «¿alguien ya la ayudó?» — null si aún no se preguntó. */
    answer?: PriorHelpAnswer | null;
    checkedAt?: string | null;
  };
  /** Design spec §08: marca de escalamiento; el caso sigue siendo del navigator. */
  escalation?: {
    escalatedAt: string | null;
    reason: string | null;
  };
  gate: WorkspaceGate;
  forms: FormLedgerRow[];
  services: WorkspaceService[];
  appointments: WorkspaceAppointment[];
  followups: WorkspaceFollowup[];
  sessions: WorkspaceSession[];
  outreachExhausted?: boolean;
  outreachExhaustedAt?: string | null;
  /** Present if the backend surfaces the config key on the payload. */
  navFindhelpBaseUrl?: string | null;
}

/**
 * PD-3: las tres cards de evidencia irresoluble (outreach_pending /
 * reimbursement_pending / blocked) murieron con el pivot del CEO 2026-08-16.
 * Un ?filter= viejo con esos valores cae a 'cases' en el dashboard.
 */
export const DASHBOARD_FILTERS = [
  'cases',
  // E-16 «EN CURSO»: sesión mía abierta o de hoy sin su tracker.
  'in_progress_today',
  'appointments_today',
  'contacts_pending',
  'followups_due',
  'no_shows',
  'ready_to_close',
  'attempts_exhausted',
  // Design spec §01 «Esperando a otro»: referral afuera, proveedor sin mover.
  'waiting_provider',
] as const;
export type DashboardFilter = (typeof DASHBOARD_FILTERS)[number];

/**
 * Una fila del dashboard. E-35 (decisión de Edd, 2026-10-21: «Descifrar solo
 * lo visible»): ya NO trae nombre, apellido ni teléfono — el servidor cuenta y
 * ordena sin descifrar. Los nombres de las filas que se ven los trae
 * `useCaseLabels`; la búsqueda, `navigatorApi.caseSearch`.
 */
export interface DashboardItem {
  id: string;
  clientId: string;
  status: string;
  priority: string;
  slaDeadline: string | null;
  nextAppointment: string | null;
  positiveDomains: string[];
  categories: string[];
  pendingEvidence: number;
  outreachExhausted?: boolean;
  // Design spec §01 — lo que cada fila necesita para decir POR QUÉ está ahí.
  firstContactAt?: string | null;
  /** Intentos del CICLO actual (después del último desbloqueo). */
  cycleAttempts?: number;
  maxAttempts?: number;
  noVoicemail?: boolean;
  createdAt?: string | null;
  // E-16 «EN CURSO» — lo que la fila necesita para decir cómo retomar.
  openSession?: boolean;
  retryableSession?: boolean;
  todayMinutes?: number;
}

export interface NavigatorDashboard {
  counts: Record<string, number>;
  items: DashboardItem[];
}

/**
 * E35-D · las cuatro secciones de «Hoy», en el orden en que se llenan y se
 * dibujan (cada caso UNA vez, en la más urgente). La agrupación vive ahora en
 * el servidor (`agruparHoy`, portada tal cual desde esta pantalla).
 */
export const HOY_SECCIONES = ['encurso', 'now', 'appts', 'uncontacted'] as const;
export type HoySeccionKey = (typeof HOY_SECCIONES)[number];

/** Una sección (o la vista filtrada) ya paginada por el servidor. */
export interface HoySeccion {
  /** Cuántos casos tiene la sección en TOTAL (el kicker y el saludo lo dicen). */
  total: number;
  /** La página que el servidor devolvió de verdad (fuera de rango → la última). */
  page: number;
  rows: DashboardItem[];
}

/** `GET me/dashboard?vista=paginada…` (E35-D). */
export interface HoyPaginado {
  vista: 'paginada';
  pageSize: number;
  hoy: string;
  counts: Record<string, number>;
  /** Con `filter`, las secciones vienen solo con su total (`rows: []`). */
  sections: Record<HoySeccionKey, HoySeccion>;
  filter: (HoySeccion & { key: DashboardFilter }) | null;
}

/** Qué página de cada cosa pide la pantalla. */
export interface HoyPedido {
  pages: Record<HoySeccionKey, number>;
  filter: DashboardFilter | null;
  pFilter: number;
  /** YYYY-MM-DD local (ver `hoyLocal`). */
  hoy: string;
}

/**
 * E35-D · el día LOCAL de este navegador (YYYY-MM-DD). Es la referencia que
 * `daysUntil` usaba para «SLA vencido» cuando la agrupación corría acá: viaja
 * al servidor para que el corte siga siendo el mismo día que ve quien mira.
 */
export function hoyLocal(now: Date = new Date()): string {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/** Los parámetros de la vista paginada, en el orden que documenta `parseHoyPedido`. */
export function hoyQuery(p: HoyPedido): string {
  const q = new URLSearchParams({
    vista: 'paginada',
    p_encurso: String(p.pages.encurso),
    p_now: String(p.pages.now),
    p_appts: String(p.pages.appts),
    p_uncontacted: String(p.pages.uncontacted),
    hoy: p.hoy,
  });
  if (p.filter) {
    q.set('filter', p.filter);
    q.set('p_filter', String(p.pFilter));
  }
  return q.toString();
}

/** Todas las secciones en la primera página (la agenda solo pide su filtro). */
export const HOY_PAGINAS_INICIO: Record<HoySeccionKey, number> = {
  encurso: 0, now: 0, appts: 0, uncontacted: 0,
};

/** E-35 · nombre y teléfono de UN caso propio y abierto (`me/case-labels`). */
export interface CaseLabel {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
}

/** E-35 · una fila de la búsqueda (`me/case-search`): lo que el pie de Hoy y el selector de la agenda dibujan. */
export interface CaseSearchHit extends CaseLabel {
  status: string;
  priority: string;
  /** Aparcado (intentos agotados): el pie ofrece «Desbloquear y abrir». */
  outreachExhausted: boolean;
  cycleAttempts: number;
  maxAttempts: number;
}

/** E-35 · el servidor acepta hasta 100 ids por pedido de nombres. */
export const CASE_LABELS_CHUNK = 100;

/**
 * E-35 · la búsqueda va al servidor: se espera a que la persona deje de
 * teclear un instante para no mandar un pedido por tecla.
 */
export const CASE_SEARCH_DEBOUNCE_MS = 300;

/** El valor, pero recién cuando dejó de cambiar `ms` milisegundos. */
export function useDebouncedValue<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return settled;
}

/** Lo que una fila necesita saber de su nombre. */
export interface CaseLabelsView {
  /** El nombre y teléfono, si ya llegaron (o si ya se conocían de antes). */
  get: (id: string) => CaseLabel | null;
  /** ¿Hay un pedido de nombres en vuelo? (el marcador de carga late). */
  loading: boolean;
}

/**
 * E-35 · los nombres de un conjunto VISIBLE de casos — una consulta por
 * conjunto (en tandas de 100 si el conjunto es más grande, como «Esperando al
 * proveedor» de la agenda).
 *
 * La llave cuelga del prefijo del dashboard (`['navigator','dashboard',
 * 'case-labels', ids]`), igual que el chip de E-35c: cada lugar que hoy
 * refresca el dashboard al mover un caso (o al corregir un teléfono) refresca
 * también los nombres, sin tocar esos archivos.
 *
 * Mientras llega la página nueva, lo que ya se conocía de otra página (o del
 * refresco anterior) se sigue mostrando: el nombre de una fila que no cambió
 * no parpadea. Lo que de verdad no se conoce todavía muestra el marcador.
 */
export function useCaseLabels(ids: readonly string[]): CaseLabelsView {
  const qc = useQueryClient();
  const firma = ids.join(',');
  const tandas = useMemo(() => {
    const out: string[][] = [];
    const lista = firma === '' ? [] : firma.split(',');
    for (let i = 0; i < lista.length; i += CASE_LABELS_CHUNK) {
      out.push(lista.slice(i, i + CASE_LABELS_CHUNK));
    }
    return out;
  }, [firma]);
  const results = useQueries({
    queries: tandas.map((tanda) => ({
      queryKey: NAV_KEYS.caseLabels(tanda),
      queryFn: () => navigatorApi.caseLabels(tanda),
    })),
  });
  const conocidos = new Map<string, CaseLabel>();
  for (const [, data] of qc.getQueriesData<CaseLabel[]>({ queryKey: NAV_KEYS.caseLabelsAll })) {
    for (const l of data ?? []) conocidos.set(String(l.id), l);
  }
  for (const r of results) {
    for (const l of r.data ?? []) conocidos.set(String(l.id), l);
  }
  return {
    get: (id) => conocidos.get(String(id)) ?? null,
    loading: results.some((r) => r.isPending || r.isFetching),
  };
}

/**
 * E-35c — lo único que el chip «Sesión abierta con {nombre}» del Shell
 * necesita. El nombre ya viene corto del servidor («Griselda G.»), con la
 * misma fórmula que el chip usaba acá.
 */
export interface OpenSessionChipData {
  caseId: string;
  displayName: string;
}

export const NAV_KEYS = {
  dashboard: ['navigator', 'dashboard'] as const,
  // E-35c — la llave del chip vive DEBAJO del prefijo del dashboard a
  // propósito: `invalidateQueries({ queryKey: NAV_KEYS.dashboard })` compara
  // por prefijo, así que cada lugar que hoy refresca el dashboard al mover un
  // caso (Hoy, el caso, el wizard de la EA) refresca también el chip, sin
  // tocar ninguno de esos archivos. Es OTRA consulta (otra URL, otro cache):
  // compartir el prefijo no hace que el Shell vuelva a bajar el dashboard.
  openSession: ['navigator', 'dashboard', 'open-session'] as const,
  // E-35 — nombres de un conjunto visible y búsqueda: bajo el MISMO prefijo,
  // por la misma razón que el chip (las invalidaciones existentes los
  // arrastran). La consulta va normalizada en la llave, nunca a un log.
  caseLabelsAll: ['navigator', 'dashboard', 'case-labels'] as const,
  caseLabels: (ids: readonly string[]) =>
    ['navigator', 'dashboard', 'case-labels', ids.join(',')] as const,
  caseSearch: (q: string, limit: number) =>
    ['navigator', 'dashboard', 'case-search', q, limit] as const,
  // E35-D — la vista paginada de «Hoy» (y el filtro de la agenda), bajo el
  // MISMO prefijo: cada `invalidateQueries({ queryKey: NAV_KEYS.dashboard })`
  // que ya existe la refresca sin tocar esos archivos. Una llave por
  // combinación de páginas: el refresco de 60 s pide solo lo que se ve.
  hoyAll: ['navigator', 'dashboard', 'hoy'] as const,
  hoy: (p: HoyPedido) => ['navigator', 'dashboard', 'hoy', hoyQuery(p)] as const,
  numbers: ['navigator', 'numbers'] as const,
  recentCases: (kind: string) => ['navigator', 'recent-cases', kind] as const,
  case: (id: string) => ['navigator', 'case', id] as const,
  workspace: (id: string) => ['navigator', 'workspace', id] as const,
  forms: (id: string) => ['navigator', 'case', id, 'forms'] as const,
  tracks: (id: string) => ['navigator', 'case', id, 'tracks'] as const,
  attachments: (id: string) => ['navigator', 'case', id, 'attachments'] as const,
  household: (id: string) => ['navigator', 'case', id, 'household'] as const,
};

// ---------------------------------------------------- Design spec 2026-08-19

/** Las 4 respuestas de «¿Alguien ya la ayudó con esto?» (§04). */
export const PRIOR_HELP_ANSWERS = ['none', 'partial', 'provider_working', 'full'] as const;
export type PriorHelpAnswer = (typeof PRIOR_HELP_ANSWERS)[number];

/** Canales del aviso del referido (§08). Pathway documenta; no envía. */
export const NOTIFY_CHANNELS = ['sms', 'email', 'told_in_call'] as const;
export type NotifyChannel = (typeof NOTIFY_CHANNELS)[number];

/** Motivos de escalamiento a supervisión (§08). */
export const ESCALATION_REASONS = ['units_exhausted', 'provider_no_response', 'other'] as const;
export type EscalationReason = (typeof ESCALATION_REASONS)[number];

export interface CloseSessionResult {
  closed: boolean;
  billed: boolean;
  sessionId: string;
  ledgerId?: string;
  units?: number;
  remaining?: number | null;
  noteId?: string;
  proposedFollowup?: { id: string; ruleKey: string; dueDate: string } | null;
}

/** §09 — Mis números. Todo derivado por el server; la UI solo dibuja. */
export interface MyNumbers {
  today: {
    contacts: number;
    attempts: number;
    sessions: number;
    billableSeconds: number;
    unitsL1: number;
    unitsL2: number;
    sessionsWithoutTracker: number;
  };
  overdue: {
    followups: number;
    referralsStale: number;
    referralStaleDays: number;
    noShowsUnrescheduled: number;
    untouchedCases: number;
    untouchedDays: number;
  };
  weekly: {
    contacts: { value: number; goal: number };
    received: { value: number; goal: number };
    completed: { value: number; goal: number };
  };
  portfolio: {
    total: number;
    uncontacted: number;
    working: number;
    waitingProvider: number;
    parked: number;
    readyToClose: number;
  };
  recent: {
    closedToday: number;
    completedToday: number;
    completed7d: number;
  };
}

/** §09 drill-down — los cuatro kinds que el server acepta (enum cerrado). */
export const RECENT_CASES_KINDS = [
  'closed_today', 'completed_today', 'completed_7d', 'closed_month',
] as const;
export type RecentCasesKind = (typeof RECENT_CASES_KINDS)[number];

export interface RecentCaseRow {
  id: string;
  clientId: string;
  name: string;
  status: string;
  reasonCode: string | null;
  closedAt: string | null;
}
